import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { addDaysIso, fmtDate, fmtTime, todayLocalIso } from '../../shared/dates';

/**
 * Case-Manager Compliance dashboard.
 *
 * Mental model: each case manager is responsible for a caseload, like a class
 * teacher. Management reviews on a weekly / biweekly cadence: of the clients
 * you're accountable for, who missed which services? What's the compliance %?
 * This is the screen for those conversations.
 *
 * Default window is last 7 days. Top table is the board summary (one row per
 * CM). Click a CM row to drill into per-client per-service detail + the full
 * missed-instance list — the exact data the CM uses to follow up with each
 * client by name.
 */
export function CaseManagerComplianceView() {
  const today = todayLocalIso();
  const [from, setFrom] = useState(addDaysIso(today, -7));
  const [to, setTo] = useState(today);
  const [openCmId, setOpenCmId] = useState<string | null>(null);

  const { data: board, isLoading } = useQuery({
    queryKey: ['cm-compliance', from, to],
    queryFn: () =>
      api
        .get('/reports/case-manager-compliance', { params: { from, to } })
        .then((r) => r.data),
  });

  const sorted = useMemo(() => {
    const rows = (board?.rows ?? []) as any[];
    return [...rows].sort((a, b) => a.compliancePct - b.compliancePct);
  }, [board]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Case manager compliance</h2>
          <p className="text-xs text-ink-subtle">
            Compliance % = (attended + late + excused) ÷ (above + no-show + client-cancelled).
            Clinic-cancelled and rescheduled rows are excluded from the denominator. Sorted by
            lowest compliance first — that's where the conversation starts.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <DateInput label="From" value={from} onChange={setFrom} />
          <DateInput label="To" value={to} onChange={setTo} />
          <button
            className="btn-secondary h-9"
            onClick={() => {
              setFrom(addDaysIso(today, -7));
              setTo(today);
            }}
          >
            Last 7d
          </button>
          <button
            className="btn-secondary h-9"
            onClick={() => {
              setFrom(addDaysIso(today, -14));
              setTo(today);
            }}
          >
            Last 14d
          </button>
          <button
            className="btn-secondary h-9"
            onClick={() => {
              setFrom(addDaysIso(today, -30));
              setTo(today);
            }}
          >
            Last 30d
          </button>
        </div>
      </header>

      <section className="card overflow-hidden">
        {isLoading ? (
          <p className="p-4 text-sm text-ink-subtle">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="p-6 text-center text-sm text-ink-subtle">
            No active case managers with an assigned caseload yet. Assign clients to a community-provider
            from intake or the client detail page.
          </p>
        ) : (
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Case manager</th>
                <th className="px-3 py-2 text-right">Clients</th>
                <th className="px-3 py-2 text-right">Scheduled</th>
                <th className="px-3 py-2 text-right">Attended</th>
                <th className="px-3 py-2 text-right">Late</th>
                <th className="px-3 py-2 text-right">No-show</th>
                <th className="px-3 py-2 text-right">Client cancel</th>
                <th className="px-3 py-2 text-right">Excused</th>
                <th className="px-3 py-2 text-right">Compliance</th>
                <th className="px-3 py-2 text-right">Phone</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r: any) => (
                <tr key={r.caseManagerId} className="border-t border-line-subtle hover:bg-surface-subtle/40">
                  <td className="px-3 py-2 font-medium">{r.displayName}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.clientCount}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.scheduled}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.attended}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.late}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-700">{r.noShow}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{r.cancelledClient}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.excused}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={pctCls(r.compliancePct)}>{r.compliancePct}%</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-2xs text-ink-subtle">
                    {r.cellPhone ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      className="text-brand-700 hover:underline"
                      onClick={() => setOpenCmId(r.caseManagerId)}
                    >
                      Drill in →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {openCmId && (
        <CaseManagerDetailModal
          caseManagerId={openCmId}
          from={from}
          to={to}
          onClose={() => setOpenCmId(null)}
        />
      )}
    </div>
  );
}

function CaseManagerDetailModal({
  caseManagerId,
  from,
  to,
  onClose,
}: {
  caseManagerId: string;
  from: string;
  to: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['cm-compliance-detail', caseManagerId, from, to],
    queryFn: () =>
      api
        .get(`/reports/case-manager-compliance/${caseManagerId}`, { params: { from, to } })
        .then((r) => r.data),
  });

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-ink/40 p-4" onClick={onClose}>
      <div
        className="card w-full max-w-5xl max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">
              {data?.caseManager?.displayName ?? 'Case manager'} — compliance review
            </h3>
            <p className="text-xs text-ink-subtle">
              {fmtDate(from)} → {fmtDate(to)} · {data?.caseManager?.clientCount ?? 0} clients ·
              overall compliance{' '}
              <span className={pctCls(data?.overall?.compliancePct ?? 0)}>
                {data?.overall?.compliancePct ?? 0}%
              </span>
              {data?.caseManager?.cellPhone && (
                <>
                  {' · '}
                  <span className="font-mono">{data.caseManager.cellPhone}</span>
                </>
              )}
            </p>
          </div>
          <button className="btn-ghost h-8" onClick={onClose}>
            ✕
          </button>
        </header>

        {isLoading ? (
          <p className="text-sm text-ink-subtle">Loading…</p>
        ) : (
          <div className="space-y-5">
            {(data?.clients ?? []).map((c: any) => (
              <section key={c.clientId} className="rounded border border-line-subtle">
                <header className="flex flex-wrap items-center justify-between gap-2 bg-surface-subtle px-3 py-2">
                  <div>
                    <span className="font-mono text-brand-700">{c.cohortId}</span>
                    <span className="ml-2 font-medium">{c.name}</span>
                    {c.externalRef && (
                      <span className="ml-2 text-2xs text-ink-subtle">EMR {c.externalRef}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-ink-subtle">
                      {c.overall.scheduled} scheduled · {c.overall.attended + c.overall.late} attended ·{' '}
                      {c.overall.noShow + c.overall.cancelledClient} missed
                    </span>
                    <span className={pctCls(c.overall.compliancePct)}>
                      {c.overall.compliancePct}%
                    </span>
                  </div>
                </header>
                <div className="grid gap-3 p-3 md:grid-cols-2">
                  <div>
                    <h4 className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
                      By service
                    </h4>
                    {c.byService.length === 0 ? (
                      <p className="text-xs text-ink-subtle">No instances in window.</p>
                    ) : (
                      <table className="w-full text-2xs">
                        <thead className="text-ink-subtle">
                          <tr>
                            <th className="text-left">Service</th>
                            <th className="text-right">Sched</th>
                            <th className="text-right">Att+Late</th>
                            <th className="text-right">Miss</th>
                            <th className="text-right">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.byService.map((s: any) => (
                            <tr key={s.serviceType} className="border-t border-line-subtle/60">
                              <td className="py-1">{s.serviceLabel}</td>
                              <td className="py-1 text-right tabular-nums">{s.scheduled}</td>
                              <td className="py-1 text-right tabular-nums">
                                {s.attended + s.late}
                              </td>
                              <td className="py-1 text-right tabular-nums text-rose-700">
                                {s.noShow + s.cancelledClient}
                              </td>
                              <td className="py-1 text-right">
                                <span className={pctCls(s.compliancePct)}>{s.compliancePct}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div>
                    <h4 className="mb-1 text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
                      Missed instances ({c.missed.length})
                    </h4>
                    {c.missed.length === 0 ? (
                      <p className="text-xs text-emerald-700">All sessions accounted for ✓</p>
                    ) : (
                      <ul className="space-y-1 text-2xs">
                        {c.missed.map((m: any) => (
                          <li
                            key={m.instanceId}
                            className="flex items-center justify-between gap-2 rounded bg-rose-50/60 px-2 py-1"
                          >
                            <span className="font-mono text-ink-subtle">
                              {fmtDate(m.date)} {fmtTime(m.startMin)}
                            </span>
                            <span className="flex-1 truncate">
                              {m.serviceLabel} · {m.resourceName}
                            </span>
                            <span
                              className={
                                m.status === 'NO_SHOW' ? 'pill-danger' : 'pill-warn'
                              }
                            >
                              {m.status === 'NO_SHOW' ? 'No-show' : 'Client cancel'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </section>
            ))}
            {(data?.clients ?? []).length === 0 && (
              <p className="p-4 text-center text-sm text-ink-subtle">
                No clients assigned to this case manager.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col text-2xs text-ink-subtle">
      {label}
      <input
        type="date"
        className="input h-9"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function pctCls(pct: number): string {
  return clsx(
    'inline-block rounded px-2 py-0.5 text-2xs font-semibold tabular-nums',
    pct >= 90
      ? 'bg-emerald-100 text-emerald-800'
      : pct >= 75
        ? 'bg-amber-100 text-amber-800'
        : 'bg-rose-100 text-rose-800',
  );
}
